import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Repeat } from "lucide-react";
import { MemberAvatar } from "@/components/Atoms";
import { ItemActionSheet } from "@/components/ItemActionSheet";
import { useAuth } from "@/context/AuthContext";
import { api, getCached } from "@/lib/api";
import { useLongPress } from "@/lib/useLongPress";
import { optimisticAction } from "@/lib/optimistic";
import { fmtRelativeDate } from "@/lib/format";
import { EmptyHint, ScopeChips, useScope } from "./Shared";

export function ChoresView({ refreshKey }) {
  const navigate = useNavigate();
  const { family, members: familyMembers, user } = useAuth();
  const { scope, setScope, me, scopePersonId } = useScope(family, familyMembers, user?.id);
  // Cache-first paint: instant from last-known list, keyed by scope so
  // switching chips doesn't flash stale data.
  const cacheKey = family ? `/families/${family.id}/chores?scope=${scope}` : null;
  const cached = cacheKey ? getCached(cacheKey) : null;
  const [chores, setChores] = useState(cached || []);
  const [members, setMembers] = useState(familyMembers || []);
  const [loading, setLoading] = useState(!cached);
  const [actionTarget, setActionTarget] = useState(null);

  const load = async () => {
    if (!family) return;
    try {
      // Members come from shared AuthContext — no per-screen refetch.
      const params = scopePersonId ? { assignee: scopePersonId } : {};
      const ch = await api.get(`/families/${family.id}/chores`, { params });
      setChores(ch.data);
      setMembers(familyMembers || []);
    } finally {
      setLoading(false);
    }
  };
  // refetch when family / scope / refreshKey changes
  useEffect(() => { load(); }, [family, refreshKey, scope]);
  useEffect(() => { setMembers(familyMembers || []); }, [familyMembers]);

  const onToggle = (c) => {
    // Tap-cycle: open → submitted → approved → open (undo).
    // The "undo" branch reopens an approved chore — strike-through clears
    // instantly, server reconciles via /reopen. Mirrors the row's natural
    // gesture: tap again to take it back.
    let action, nextStatus;
    if (c.status === "open") { action = "submit"; nextStatus = "submitted"; }
    else if (c.status === "submitted") { action = "approve"; nextStatus = "approved"; }
    else if (c.status === "approved") { action = "reopen"; nextStatus = "open"; }
    else return;
    return optimisticAction({
      apply: () => setChores((arr) => arr.map((x) => (x.id === c.id ? { ...x, status: nextStatus } : x))),
      undo:  () => setChores((arr) => arr.map((x) => (x.id === c.id ? { ...x, status: c.status }   : x))),
      network: () => api.post(`/families/${family.id}/chores/${c.id}/${action}`),
    });
  };

  const chips = (
    <ScopeChips scope={scope} onScopeChange={setScope} members={members} me={me} />
  );

  if (loading)
    return (
      <div className="space-y-3" data-testid="chores-view">
        {chips}
        <div className="kinly-card h-32 skeleton" />
      </div>
    );
  if (chores.length === 0)
    return (
      <div className="space-y-3" data-testid="chores-view">
        {chips}
        <EmptyHint
          eyebrow="Chores"
          headline={scope === "me" ? "Your chore board's clean." : "No chores in this view."}
          body="Add the dishwasher rotation, the lunchbox check, the dog walk."
        />
      </div>
    );

  return (
    <div className="space-y-3" data-testid="chores-view">
      {chips}
      <article className="kinly-card">
        <ul className="divide-y divide-border/70">
          {chores.map((c) => (
            <ChoreRow
              key={c.id}
              chore={c}
              members={members}
              onToggle={() => onToggle(c)}
              onLongPress={() => setActionTarget(c)}
              onOpen={() => navigate(`/chore/${c.id}`)}
            />
          ))}
        </ul>
      </article>

      <ItemActionSheet
        open={!!actionTarget}
        kind="chore"
        item={actionTarget}
        familyId={family?.id}
        members={members}
        onChanged={load}
        onClose={() => setActionTarget(null)}
      />
    </div>
  );
}

function ChoreRow({ chore: c, members, onToggle, onLongPress, onOpen }) {
  const assignee = members.find((m) => m.id === c.assignee_person_ids?.[0]);
  const longPress = useLongPress(() => onLongPress?.());
  const dueLabel = c.due_at ? fmtRelativeDate(c.due_at) : null;
  const repeatLabel = c.recurrence?.rule;
  // Tap anywhere on the row body (NOT the checkbox) → open ChoreDetail.
  // Matches the Event/Todo pattern so users can reach the Pencil-edit flow.
  return (
    <li
      data-testid={`chore-row-${c.id}`}
      className="flex items-center gap-3 py-3 select-none tap"
      onClick={onOpen}
      {...longPress.bind}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        data-testid={`chore-toggle-${c.id}`}
        className={[
          "w-6 h-6 rounded-md border-2 tap flex items-center justify-center shrink-0",
          c.status === "approved"
            ? "bg-primary border-primary text-primary-foreground"
            : c.status === "submitted"
            ? "bg-kinly-mustard/30 border-kinly-mustard"
            : "border-border",
        ].join(" ")}
        aria-label={`Mark ${c.title}`}
      >
        {c.status === "approved" && <Check className="w-4 h-4" />}
      </button>
      <div className="flex-1 min-w-0">
        <p
          className={[
            "text-[15px] truncate",
            c.status === "approved" ? "line-through text-muted-foreground" : "",
          ].join(" ")}
        >
          {c.title}
        </p>
        <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground">
          {assignee && (
            <>
              <MemberAvatar member={assignee} size={18} ring={false} />
              <span>{assignee.display_name}</span>
            </>
          )}
          {dueLabel && (
            <span data-testid={`chore-due-${c.id}`} className="ml-1">· {dueLabel}</span>
          )}
          {repeatLabel && (
            <span data-testid={`chore-repeat-${c.id}`} className="ml-1 inline-flex items-center gap-0.5">
              <Repeat className="w-3 h-3" />
              {repeatLabel}
            </span>
          )}
          {c.status === "submitted" && (
            <span className="text-kinly-mustard ml-1">waiting</span>
          )}
        </div>
      </div>
    </li>
  );
}
